// src/components/contact.jsx
// ContactDetail modal - shows contact info, deals, and activity feed.

import { useState, useEffect } from "react";
import * as HS from "../hubspot.js";
import { STAGE_COLORS } from "../constants.js";
import { Btn, Chip, FI, LinkBtn, Modal, Spin, ActivitySummary } from "./ui.jsx";
import { OwnerBadge } from "./owners.jsx";
import { EmailCompose, LogActivity } from "./activity.jsx";

export function ContactDetail({contact, company, users, currentUser, onClose, onOpenCompany}) {
  const [acts,  setActs]  = useState([]);
  const [loadA, setLoadA] = useState(true);
  const [showEmail, setShowEmail] = useState(false);
  const [showLog,   setShowLog]   = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadA(true);
      try {
        const arr = await HS.getActivitiesForContact(contact.id, company?.id || null);
        if (!cancelled) setActs(Array.isArray(arr) ? arr : []);
      } catch {
        if (!cancelled) setActs([]);
      }
      if (!cancelled) setLoadA(false);
    })();
    return () => { cancelled = true; };
  }, [contact.id]);

  const handleLogged = newAct => setActs(prev => [{...newAct, fresh: true}, ...prev]);

  return (
    <>
      <Modal title={contact.name} onClose={onClose} wide>
        <div className="g2" style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:24}}>
          <div style={{display:"flex",flexDirection:"column",gap:18}}>
            <div style={{padding:"16px 18px",borderRadius:14,background:"var(--warm1)",border:"1px solid var(--warm2)"}}>
              <div style={{fontSize:12,fontWeight:600,color:"var(--muted)",textTransform:"uppercase",letterSpacing:.5,marginBottom:12}}>Contact Info</div>
              {[["✉ Email", contact.email], ["💼 Role", contact.role]].map(([k,v]) => (
                <div key={k} style={{display:"flex",justifyContent:"space-between",fontSize:13,padding:"6px 0",borderBottom:"1px solid var(--warm2)"}}>
                  <span style={{color:"var(--muted)"}}>{k}</span>
                  <span style={{fontWeight:500}}>{v || "-"}</span>
                </div>
              ))}
              {company && (
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",fontSize:13,padding:"6px 0",borderBottom:"1px solid var(--warm2)"}}>
                  <span style={{color:"var(--muted)"}}>🏢 Company</span>
                  <LinkBtn onClick={()=>{onClose(); onOpenCompany(company);}}>{company.properties.name}</LinkBtn>
                </div>
              )}
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",fontSize:13,padding:"8px 0",marginTop:2}}>
                <span style={{color:"var(--muted)"}}>👤 Owner</span>
                <OwnerBadge ownerId={contact.ownerId} users={users}/>
              </div>
            </div>
            {company && (company.deals || []).length > 0 && (
              <div>
                <div style={{fontSize:12,fontWeight:600,color:"var(--muted)",textTransform:"uppercase",letterSpacing:.5,marginBottom:10}}>Company Deals</div>
                {company.deals.map(d => (
                  <div key={d.id} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"10px 14px",borderRadius:12,border:"1px solid var(--warm2)",background:"#fff",marginBottom:8}}>
                    <div>
                      <div style={{fontWeight:600,fontSize:13,marginBottom:3}}>{d.name}</div>
                      <Chip label={d.stage} color={STAGE_COLORS[d.stage] || "#999"}/>
                    </div>
                    <div style={{fontWeight:700,color:"var(--brown)",fontFamily:"Barlow Condensed,sans-serif"}}>${d.amount?.toLocaleString()}</div>
                  </div>
                ))}
              </div>
            )}
            <div style={{display:"flex",gap:8}}>
              <Btn variant="soft" onClick={()=>setShowEmail(true)} style={{fontSize:12}}>✉ Email</Btn>
              <Btn variant="soft" onClick={()=>setShowLog(true)}   style={{fontSize:12}}>📋 Log</Btn>
            </div>
          </div>
          <div style={{display:"flex",flexDirection:"column",gap:12}}>
            <div style={{fontSize:12,fontWeight:600,color:"var(--muted)",textTransform:"uppercase",letterSpacing:.5}}>Activity</div>
            <div style={{overflowY:"auto",maxHeight:380}}>
              {loadA
                ? <div style={{textAlign:"center",padding:40,color:"var(--muted)"}}><Spin/></div>
                : acts.length === 0
                  ? <div style={{padding:"30px 0",textAlign:"center",color:"var(--muted)",fontSize:13,fontStyle:"italic"}}>No recorded activities yet.</div>
                  : acts.map((a, i) => (
                    <div key={i} style={{display:"flex",gap:12,padding:"12px 0",borderBottom:"1px solid var(--warm1)"}}>
                      <div style={{width:32,height:32,borderRadius:99,background:"var(--warm1)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:15,flexShrink:0}}>
                        {a.type === "note" ? "📝" : a.type === "call" ? "📞" : "✉"}
                      </div>
                      <div>
                        <div style={{fontSize:12,color:"var(--muted)",marginBottom:3}}>
                          {a.date} · <span style={{textTransform:"capitalize"}}>{a.type}</span>
                        </div>
                        <div style={{fontSize:13,lineHeight:1.5}}><ActivitySummary activity={a} fallback={a.summary}/></div>
                      </div>
                    </div>
                  ))
              }
            </div>
          </div>
        </div>
      </Modal>
      {showEmail && <EmailCompose defaultTo={contact.email} defaultName={contact.name} onClose={()=>setShowEmail(false)}/>}
      {showLog && <LogActivity company={company || {properties:{name:""}}} contact={contact} onClose={()=>setShowLog(false)} onLogged={handleLogged} currentUser={currentUser}/>}
    </>
  );
}

// ── AddContactModal ──────────────────────────────────────────────────────────
// Create a new contact, optionally associated to a company. ALL fields
// required (per user spec). Performs email-uniqueness check via HubSpot before
// creating — if email exists, blocks save and shows the existing contact.
//
// Usage:
//   - From inside a CompanyDetail: pass `companies={[thatCompany]}` and
//     `defaultCompanyId={thatCompany.id}`. Dropdown is locked to that company.
//   - From top-level Contacts tab: pass `companies={allCompanies}`. Rep
//     selects a company from the dropdown.
export function AddContactModal({companies, defaultCompanyId, currentUser, onClose, onCreated}) {
  const [firstName, setFirstName] = useState("");
  const [lastName,  setLastName]  = useState("");
  const [email,     setEmail]     = useState("");
  const [phone,     setPhone]     = useState("");
  const [role,      setRole]      = useState("");
  const [companyId, setCompanyId] = useState(defaultCompanyId || (companies?.[0]?.id || ""));
  const [saving,    setSaving]    = useState(false);
  const [err,       setErr]       = useState("");
  const [existing,  setExisting]  = useState(null);

  // Whether the company dropdown should be locked (single option) — true when
  // the modal is opened from inside a company detail
  const lockCompany = !!defaultCompanyId && (companies?.length || 0) <= 1;

  const allFilled = !!(firstName.trim() && lastName.trim() && email.trim() && phone.trim() && role.trim() && companyId);

  const validateEmail = (s) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s.trim());

  const handleSave = async () => {
    setErr("");
    setExisting(null);
    if (!allFilled) { setErr("All fields are required."); return; }
    if (!validateEmail(email)) { setErr("Please enter a valid email address."); return; }

    setSaving(true);
    try {
      // Email-existence check
      const found = await HS.findContactByEmail(email);
      if (found) {
        setExisting(found);
        setErr("A contact with this email already exists.");
        setSaving(false);
        return;
      }
      const newContact = await HS.createContact(
        { firstName, lastName, email, phone, role },
        companyId,
      );
      onCreated(newContact, companyId);
      onClose();
    } catch (e) {
      setErr("Could not save: " + e.message);
    }
    setSaving(false);
  };

  return (
    <Modal title="+ New Contact" onClose={onClose} wide>
      <div style={{display:"flex",flexDirection:"column",gap:14}}>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
          <FI label="First Name *" value={firstName} onChange={setFirstName} placeholder="Jane"/>
          <FI label="Last Name *"  value={lastName}  onChange={setLastName}  placeholder="Smith"/>
        </div>
        <FI label="Email *" value={email} onChange={setEmail} placeholder="jane@example.com"/>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
          <FI label="Phone *"     value={phone} onChange={setPhone} placeholder="(555) 123-4567"/>
          <FI label="Job Title *" value={role}  onChange={setRole}  placeholder="Sales Manager"/>
        </div>
        <div style={{display:"flex",flexDirection:"column",gap:5}}>
          <label style={{fontSize:11,fontWeight:600,color:"var(--muted)",textTransform:"uppercase",letterSpacing:.5}}>Company *</label>
          <select value={companyId} onChange={e=>setCompanyId(e.target.value)} disabled={lockCompany}
            style={{padding:"10px 14px",borderRadius:10,border:"1.5px solid var(--warm2)",background:lockCompany?"var(--warm1)":"#fff",fontSize:13,outline:"none",fontFamily:"Barlow,sans-serif"}}>
            <option value="">— Select —</option>
            {(companies || []).map(c => <option key={c.id} value={c.id}>{c.properties?.name || c.id}</option>)}
          </select>
        </div>

        {err && (
          <div style={{padding:"12px 14px",borderRadius:10,background:"#fdecea",border:"1px solid var(--red)",color:"var(--red)",fontSize:13}}>
            {err}
            {existing && (
              <div style={{marginTop:6,fontSize:12}}>
                <strong>Existing:</strong>{" "}
                {(existing.properties?.firstname || "") + " " + (existing.properties?.lastname || "")} ({existing.properties?.email})
              </div>
            )}
          </div>
        )}

        <div style={{display:"flex",gap:10,justifyContent:"flex-end",marginTop:6}}>
          <Btn variant="ghost" onClick={onClose} disabled={saving}>Cancel</Btn>
          <Btn variant="primary" onClick={handleSave} disabled={saving || !allFilled}>
            {saving ? <><Spin/> Saving...</> : "Save Contact"}
          </Btn>
        </div>
      </div>
    </Modal>
  );
}
